import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Search, ShoppingBag, User, Menu, X, ChevronDown, LogOut } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useUser } from '../../context/UserContext';
//import Button from '../common/Button';
import logo2 from '../../assets/images/logo2.jpeg';
import productService from '../../services/productService';

const Navbar = () => {
    const navigate = useNavigate();
    const { cartCount } = useCart();
    const { user, logout } = useUser();
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [activeDropdown, setActiveDropdown] = useState(null);
    const [isMobileCollectionsOpen, setIsMobileCollectionsOpen] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [allProducts, setAllProducts] = useState([]);
    const [allCategories, setAllCategories] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [filteredCategories, setFilteredCategories] = useState([]);
    const [showSearchDropdown, setShowSearchDropdown] = useState(false);

    useEffect(() => {
        productService.getProducts().then(setAllProducts).catch(console.error);
        productService.getCategories().then(setAllCategories).catch(console.error);
    }, []);

    const normalizeString = (str) => {
        if (!str) return '';
        return String(str).normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
    };

    useEffect(() => {
        if (searchQuery.trim().length > 0) {
            const query = normalizeString(searchQuery);

            const matchedCats = allCategories.filter(cat => normalizeString(cat.name).includes(query));
            setFilteredCategories(matchedCats.slice(0, 3));

            const matchedProds = allProducts.filter(p => {
                const nameMatch = normalizeString(p.name).includes(query);
                const catMatch = normalizeString(String(p.category)).includes(query);
                const catNameMatch = p.category_name ? normalizeString(p.category_name).includes(query) : false;
                return nameMatch || catMatch || catNameMatch;
            });
            setFilteredProducts(matchedProds.slice(0, 5));
            setShowSearchDropdown(true);
        } else {
            setFilteredProducts([]);
            setFilteredCategories([]);
            setShowSearchDropdown(false);
        }
    }, [searchQuery, allProducts, allCategories]);

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 20);
        };

        const handleClickOutside = (event) => {
            if (activeDropdown && !event.target.closest('.nav-dropdown-container')) {
                setActiveDropdown(null);
            }
        };

        window.addEventListener('scroll', handleScroll);
        document.addEventListener('click', handleClickOutside);

        return () => {
            window.removeEventListener('scroll', handleScroll);
            document.removeEventListener('click', handleClickOutside);
        };
    }, [activeDropdown]);

    const handleSearch = (e) => {
        e.preventDefault();
        if (searchQuery.trim()) {
            navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
            setIsMobileMenuOpen(false);
        }
    };

    const navLinks = [
        { name: 'Accueil', href: '/' },
        {
            name: 'Mobiliers',
            href: '#',
            subItems: [
                { name: 'Salon', query: 'Salon' },
                { name: 'Chambre', query: 'Chambre' },
                { name: 'Cuisine', query: 'Cuisine' },
                { name: 'Salle de bain', query: 'Salle de bain' },
                { name: 'Bureau', query: 'Bureau' },
                { name: 'Divers', query: 'Divers' },
            ]
        },
        {
            name: 'Electroménagers',
            href: '#',
            subItems: [
                { name: 'Réfrigérateurs', query: 'Réfrigérateur' },
                { name: 'Congélateurs', query: 'Congélateur' },
                { name: 'Cuisinières', query: 'Cuisinière' },
                { name: 'Machines à laver', query: 'Machine à laver' },
                { name: 'Teléviseurs', query: 'Téléviseur' },
                { name: 'Micro-ondes', query: 'Micro-ondes' },
                { name: 'Aspirateurs', query: 'Aspirateur' },
                { name: 'Climatiseurs', query: 'Climatiseur' },
                { name: 'Friteuses', query: 'Friteuse' },
            ]
        },
        {
            name: 'Décors',
            href: '#',
            subItems: [
                { name: 'Décoration murale', query: 'Décoration murale' },
                { name: 'Fleurs & Plantes Decoratives', query: 'Fleurs & Plantes' },
                { name: 'Decorations de table', query: 'Décoration de table' },
                { name: 'Luminaires decoratifs', query: 'Luminaires' },
                { name: 'Deco evenementielle', query: 'Décoration événementielle' },
                { name: 'Décoration salon', query: 'Décoration salon' },
                { name: 'Décoration chambre', query: 'Décoration chambre' },
                { name: 'Décoration bureau', query: 'Décoration bureau' },
                { name: 'Décoration exterieure', query: 'Décoration extérieure' },
                { name: 'Décoration cuisine', query: 'Décoration cuisine' },
                { name: 'Décoration salle de bain', query: 'Décoration salle de bain' },

            ]
        },
        {
            name: 'Revêtements', href: '#', subItems: [
                { name: 'Revêtements muraux', query: 'Revêtement muraux' },
                { name: 'Revêtements sols', query: 'Revêtement sols' },
                { name: 'Revêtements plafonds', query: 'Revêtement plafonds' },
            ]
        },
        { name: 'Showroom', href: '/showroom' },
        { name: 'Contacts', href: '/contact' },
    ];

    return (
        <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${isScrolled ? 'py-4' : 'py-6'}`}>
            <div className="container mx-auto px-6">
                <div className="relative flex items-center justify-between px-6 py-3 rounded-full bg-white shadow-lg border border-slate-100">
                    {/* Logo */}
                    <Link to="/" className="flex items-center group shrink-0">
                        <div className="h-12 sm:h-16 w-auto min-w-[120px] sm:min-w-[180px] overflow-hidden rounded-lg">
                            <img src={logo2} alt="SHOWROOM Logo" className="h-full w-auto object-contain transition-transform group-hover:scale-105" />
                        </div>
                    </Link>

                    {/* Desktop Navigation */}
                    <div className="hidden lg:flex items-center gap-4 xl:gap-8 mx-4">
                        {navLinks.map((link) => (
                            <div
                                key={link.name}
                                className="relative nav-dropdown-container"
                            >
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        if (link.subItems) {
                                            setActiveDropdown(activeDropdown === link.name ? null : link.name);
                                        } else if (link.href.startsWith('/#')) {
                                            const id = link.href.substring(2);
                                            if (window.location.pathname === '/') {
                                                document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                                            } else {
                                                navigate('/');
                                                setTimeout(() => {
                                                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                                                }, 100);
                                            }
                                        } else {
                                            navigate(link.href);
                                        }
                                    }}
                                    className="text-sm font-medium text-slate-600 hover:text-brand transition-colors flex items-center gap-1"
                                >
                                    {link.name}
                                    {link.subItems && (
                                        <ChevronDown className={`w-4 h-4 transition-transform duration-300 ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                                    )}
                                </button>

                                {/* Dropdown Menu */}
                                {
                                    link.subItems && (
                                        <div className={`absolute top-full left-0 mt-4 w-56 bg-white rounded-2xl shadow-xl border border-slate-100 py-4 transition-all duration-300 transform origin-top ${activeDropdown === link.name ? 'opacity-100 scale-100' : 'opacity-0 scale-95 pointer-events-none'}`}>
                                            <div className="flex flex-col">
                                                {link.subItems.map((subItem) => (
                                                    <button
                                                        key={subItem.name}
                                                        onClick={() => {
                                                            navigate(`/search?q=${encodeURIComponent(subItem.query)}`);
                                                            setActiveDropdown(null);
                                                        }}
                                                        className="px-6 py-2.5 text-sm text-left text-slate-600 hover:text-brand hover:bg-slate-50 transition-colors"
                                                    >
                                                        {subItem.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    )
                                }
                            </div>
                        ))}
                    </div>

                    {/* Actions */}
                    <div className="flex items-center gap-1 sm:gap-2">

                        <Link to="/cart" className="p-2.5 text-slate-600 hover:bg-slate-50 rounded-full transition-colors relative">
                            <ShoppingBag className="w-5 h-5" />
                            {cartCount > 0 && (
                                <span className="absolute top-1 right-1 w-4 h-4 bg-brand text-white text-[10px] flex items-center justify-center rounded-full">
                                    {cartCount}
                                </span>
                            )}
                        </Link>

                        {user ? (
                            <button
                                onClick={() => {
                                    logout();
                                    navigate('/');
                                }}
                                className="p-1.5"
                                title="Se déconnecter"
                            >
                                <div className="w-10 h-10 bg-brand rounded-full flex items-center justify-center text-white hover:bg-red-500 transition-colors">
                                    <LogOut className="w-5 h-5 translate-x-0.5" />
                                </div>
                            </button>
                        ) : (
                            <button
                                onClick={() => navigate('/auth')}
                                className="p-2.5 text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                                title="Se connecter"
                            >
                                <User className="w-5 h-5" />
                            </button>
                        )}

                        <button
                            className="lg:hidden p-2.5 text-slate-600 hover:bg-slate-50 rounded-full transition-colors"
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        >
                            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                        </button>
                    </div>
                </div>

                {/* Mobile menu */}
                <div className={`
          lg:hidden absolute top-full left-6 right-6 mt-4 p-8 bg-white/95 backdrop-blur-xl rounded-[2.5rem] shadow-2xl border border-slate-100/50
          transition-all duration-500 transform origin-top
          ${isMobileMenuOpen ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-95 -translate-y-4 pointer-events-none'}
        `}>
                    <div className="flex flex-col gap-4">
                        <div className="relative mb-4 z-50">
                            <form onSubmit={handleSearch} className="relative">
                                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                                <input
                                    type="text"
                                    value={searchQuery}
                                    onChange={(e) => setSearchQuery(e.target.value)}
                                    onFocus={() => { if (searchQuery.trim().length > 0) setShowSearchDropdown(true); }}
                                    onBlur={() => setTimeout(() => setShowSearchDropdown(false), 200)}
                                    placeholder="Rechercher..."
                                    className="w-full h-14 pl-12 pr-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand/20 outline-none text-lg"
                                />
                            </form>
                            {showSearchDropdown && (
                                <div 
                                    className="absolute top-16 left-0 right-0 bg-white rounded-2xl shadow-2xl border border-slate-100 overflow-hidden z-[100]"
                                    onMouseDown={(e) => e.preventDefault()}
                                >
                                    
                                    {filteredCategories.length > 0 && (
                                        <div className="border-b border-slate-100 pb-2">
                                            <h5 className="px-4 pt-3 pb-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Catégories</h5>
                                            {filteredCategories.map(cat => (
                                                <Link
                                                    key={`cat-${cat.id || cat.name}`}
                                                    to={`/category/${cat.name}`}
                                                    onClick={() => setIsMobileMenuOpen(false)}
                                                    className="flex items-center gap-3 px-4 py-2 hover:bg-slate-50 transition-colors"
                                                >
                                                    <div className="w-8 h-8 rounded-full bg-brand/10 flex items-center justify-center text-brand shrink-0">
                                                        <Search className="w-4 h-4" />
                                                    </div>
                                                    <span className="font-bold text-brand-dark text-sm">{cat.name}</span>
                                                </Link>
                                            ))}
                                        </div>
                                    )}

                                    {filteredProducts.length > 0 ? (
                                        <div className="pt-2">
                                            <h5 className="px-4 pb-2 pt-2 text-xs font-bold text-slate-400 uppercase tracking-wider">Produits</h5>
                                            <div className="max-h-60 overflow-y-auto">
                                                {filteredProducts.map(product => (
                                                    <Link
                                                        key={product.id}
                                                        to={`/product/${product.id}`}
                                                        onClick={() => setIsMobileMenuOpen(false)}
                                                        className="flex items-center gap-3 px-4 py-3 hover:bg-slate-50 transition-colors border-b border-slate-50 last:border-0"
                                                    >
                                                        <div className="w-12 h-12 rounded-lg overflow-hidden shrink-0 bg-slate-100">
                                                            <img src={product.image} alt={product.name} className="w-full h-full object-cover" />
                                                        </div>
                                                        <div className="flex-1 min-w-0">
                                                            <h4 className="font-bold text-sm text-brand-dark truncate">{product.name}</h4>
                                                            <p className="text-xs font-medium text-brand">{Math.floor(product.price).toLocaleString('fr-FR')} FCFA</p>
                                                        </div>
                                                    </Link>
                                                ))}
                                            </div>
                                        </div>
                                    ) : (
                                        !filteredCategories.length && (
                                            <div className="p-4 text-center text-slate-500 text-sm">
                                                Aucun résultat trouvé
                                            </div>
                                        )
                                    )}
                                </div>
                            )}
                        </div>

                        {navLinks.map((link) => (
                            <div key={link.name}>
                                {link.subItems ? (
                                    <>
                                        <button
                                            onClick={() => setIsMobileCollectionsOpen(!isMobileCollectionsOpen)}
                                            className="w-full flex items-center justify-between text-xl font-bold text-slate-900 hover:text-brand transition-colors px-2 py-1"
                                        >
                                            {link.name}
                                            <ChevronDown className={`w-6 h-6 transition-transform ${isMobileCollectionsOpen ? 'rotate-180' : ''}`} />
                                        </button>
                                        <div className={`overflow-hidden transition-all duration-500 ${isMobileCollectionsOpen ? 'max-h-64 mt-2' : 'max-h-0'}`}>
                                            <div className="flex flex-col gap-2 pl-4 border-l-2 border-slate-100">
                                                {link.subItems.map((subItem) => (
                                                    <button
                                                        key={subItem.name}
                                                        onClick={() => {
                                                            navigate(`/search?q=${encodeURIComponent(subItem.query)}`);
                                                            setIsMobileMenuOpen(false);
                                                        }}
                                                        className="text-lg text-left text-slate-600 hover:text-brand transition-colors py-1"
                                                    >
                                                        {subItem.name}
                                                    </button>
                                                ))}
                                            </div>
                                        </div>
                                    </>
                                ) : (
                                    <button
                                        onClick={() => {
                                            if (link.href.startsWith('/#')) {
                                                const id = link.href.substring(2);
                                                if (window.location.pathname === '/') {
                                                    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                                                } else {
                                                    navigate('/');
                                                    setTimeout(() => {
                                                        document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
                                                    }, 100);
                                                }
                                            } else {
                                                navigate(link.href);
                                            }
                                            setIsMobileMenuOpen(false);
                                        }}
                                        className="text-xl font-bold text-left text-slate-900 hover:text-brand transition-colors px-2 py-1"
                                    >
                                        {link.name}
                                    </button>
                                )}
                            </div>
                        ))}
                        <hr className="border-slate-100 my-2" />
                        {user ? (
                            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-2xl">
                                <div className="flex items-center gap-4">
                                    <div className="w-12 h-12 rounded-full overflow-hidden border-2 border-brand">
                                        <img src={user.avatar} alt={user.name} className="w-full h-full object-cover" />
                                    </div>
                                    <div>
                                        <p className="font-bold text-brand-dark">{user.name}</p>
                                        <p className="text-xs text-slate-500">{user.email}</p>
                                    </div>
                                </div>
                                <button
                                    onClick={() => {
                                        logout();
                                        setIsMobileMenuOpen(false);
                                        navigate('/');
                                    }}
                                    className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center text-white shadow-lg shadow-red-200"
                                >
                                    <LogOut className="w-6 h-6 translate-x-0.5" />
                                </button>
                            </div>
                        ) : (
                            <button
                                onClick={() => {
                                    setIsMobileMenuOpen(false);
                                    navigate('/auth');
                                }}
                                className="w-full flex items-center justify-between p-4 bg-slate-50 rounded-2xl text-slate-600 hover:text-brand transition-colors"
                            >
                                <span className="font-bold">Accéder à mon compte</span>
                                <div className="w-12 h-12 bg-brand rounded-full flex items-center justify-center text-white shadow-lg shadow-brand/20">
                                    <User className="w-6 h-6" />
                                </div>
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </nav >
    );
};

export default Navbar;
